import React, { useState } from 'react';
import { CreditCard, CheckCircle2, Zap, ArrowRight, XCircle, ShieldCheck, Crown, Star, Calendar, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../context/AlertContext';

const SubscriptionPayments = ({ tenantData }) => {
    const currentPlan = tenantData?.plan || 'free';
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [canceling, setCanceling] = useState(false);
    const navigate = useNavigate();
    const { showConfirm, showAlert } = useAlert();

    const subscriptionStatus = tenantData?.subscription_status || tenantData?.subscriptionStatus;
    const nextBillingDate = tenantData?.next_billing_date || tenantData?.nextBillingDate;
    const cancelAtPeriodEnd = tenantData?.cancel_at_period_end ?? tenantData?.cancelAtPeriodEnd;
    const hasActiveSubscription = subscriptionStatus === 'ACTIVE' && currentPlan !== 'free';

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
        } catch {
            return '';
        }
    };

    const handleCancelSubscription = async () => {
        const confirmed = await showConfirm(
            cancelAtPeriodEnd
                ? 'İptal işlemi geri alınacak. Aboneliğiniz otomatik yenilenmeye devam edecek.'
                : `Abonelik iptal edildiğinde ${formatDate(nextBillingDate) || 'dönem sonuna'} kadar mevcut planınızı kullanmaya devam edersiniz. Ardından planınız otomatik olarak ücretsize düşer.`,
            {
                title: cancelAtPeriodEnd ? 'İptali Geri Al' : 'Aboneliği İptal Et',
                confirmText: cancelAtPeriodEnd ? 'İptali Geri Al' : 'Evet, İptal Et',
                type: cancelAtPeriodEnd ? 'info' : 'warning'
            }
        );
        if (!confirmed) return;

        setCanceling(true);
        try {
            const res = await fetch('/api/subscription?action=cancel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenantId: tenantData?.id,
                    atPeriodEnd: !cancelAtPeriodEnd,
                    reactivate: cancelAtPeriodEnd || false,
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'İptal başarısız');
            showAlert('İşlem başarılı. Sayfayı yenileyin.', { type: 'success' });
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            showAlert(err.message || 'Bir hata oluştu', { type: 'error' });
        } finally {
            setCanceling(false);
        }
    };

    const plans = [
        {
            id: 'free',
            name: 'Başlangıç',
            price: { monthly: '0 ₺', yearly: '0 ₺' },
            description: 'QR menü ile dijital dönüşüme ilk adım',
            icon: <Zap size={20} />,
            features: [
                { name: 'QR Menü Yönetimi', included: true },
                { name: 'İşletme Profili', included: true },
                { name: 'Müşteri Yönetimi', included: false },
                { name: 'Sipariş Yönetimi', included: false },
                { name: 'Kampanya & Ödüller', included: false },
                { name: 'Analitik & Raporlar', included: false },
                { name: 'Entegrasyonlar', included: false },
                { name: 'AI Danışman', included: false }
            ]
        },
        {
            id: 'pro',
            name: 'Pro',
            price: { monthly: '1 ₺', yearly: '1 ₺' },
            description: 'Büyüyen işletmeler için tam donanımlı CRM',
            isPopular: true,
            icon: <Star size={20} />,
            features: [
                { name: 'QR Menü Yönetimi', included: true },
                { name: 'İşletme Profili & Tüm Ayarlar', included: true },
                { name: 'Sınırsız Müşteri Yönetimi', included: true },
                { name: 'Sipariş Yönetimi', included: true },
                { name: 'Kampanya & Ödüller', included: true },
                { name: 'Analitik, Stok & Muhasebe', included: true },
                { name: 'Entegrasyonlar', included: false },
                { name: 'AI Danışman', included: false }
            ]
        },
        {
            id: 'premium',
            name: 'Premium',
            price: { monthly: '1 ₺', yearly: '1 ₺' },
            description: 'AI destekli tam kapsamlı işletme yönetimi',
            icon: <Crown size={20} />,
            features: [
                { name: 'QR Menü Yönetimi', included: true },
                { name: 'İşletme Profili & Tüm Ayarlar', included: true },
                { name: 'Sınırsız Müşteri Yönetimi', included: true },
                { name: 'Sipariş Yönetimi', included: true },
                { name: 'Kampanya & Ödüller', included: true },
                { name: 'Analitik, Stok & Muhasebe', included: true },
                { name: 'Ödeme Entegrasyonları', included: true },
                { name: 'AI Danışman', included: true }
            ]
        }
    ];

    const planOrder = { free: 0, pro: 1, premium: 2 };
    const isUpgrade = (planId) => planOrder[planId] > planOrder[currentPlan];
    const isDowngrade = (planId) => planOrder[planId] < planOrder[currentPlan];

    const currentPlanData = plans.find(p => p.id === currentPlan) || plans[0];

    const handlePlanAction = (planId) => {
        if (planId === currentPlan) return;
        if (isUpgrade(planId)) {
            navigate(`/checkout?plan=${planId}`);
        }
    };

    const getButtonProps = (plan) => {
        if (plan.id === currentPlan) {
            return { label: 'Şu Anki Planınız', disabled: true, style: 'current' };
        }
        if (isUpgrade(plan.id)) {
            return { label: 'Bu Plana Geçin', disabled: false, style: 'upgrade' };
        }
        return { label: 'Mevcut Planınız Daha Üst', disabled: true, style: 'downgrade' };
    };

    return (
        <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

            {/* Abonelik Durumu Banner */}
            {hasActiveSubscription && (
                <div style={{
                    background: cancelAtPeriodEnd ? 'rgba(239, 68, 68, 0.06)' : 'rgba(16, 185, 129, 0.06)',
                    border: `1px solid ${cancelAtPeriodEnd ? 'rgba(239, 68, 68, 0.25)' : 'rgba(16, 185, 129, 0.25)'}`,
                    borderRadius: '16px',
                    padding: '1.25rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {cancelAtPeriodEnd ? (
                            <AlertTriangle size={22} style={{ color: '#ef4444', flexShrink: 0 }} />
                        ) : (
                            <Calendar size={22} style={{ color: '#10b981', flexShrink: 0 }} />
                        )}
                        <div>
                            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: cancelAtPeriodEnd ? '#b91c1c' : '#047857' }}>
                                {cancelAtPeriodEnd ? 'Aboneliğiniz dönem sonunda sona erecek' : 'Aboneliğiniz aktif'}
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                                {cancelAtPeriodEnd
                                    ? `${formatDate(nextBillingDate) || 'Dönem sonu'} tarihinden sonra planınız ücretsize düşecek. İsterseniz iptali geri alabilirsiniz.`
                                    : nextBillingDate
                                        ? `Bir sonraki otomatik ödeme: ${formatDate(nextBillingDate)}`
                                        : 'Bir sonraki ödeme tarihi henüz belirlenmedi.'}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleCancelSubscription}
                        disabled={canceling}
                        style={{
                            padding: '10px 18px',
                            borderRadius: '12px',
                            background: cancelAtPeriodEnd ? '#10b981' : 'transparent',
                            color: cancelAtPeriodEnd ? 'white' : '#b91c1c',
                            border: cancelAtPeriodEnd ? 'none' : '1px solid rgba(239, 68, 68, 0.4)',
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            cursor: canceling ? 'not-allowed' : 'pointer',
                            opacity: canceling ? 0.6 : 1,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {canceling ? 'İşleniyor...' : cancelAtPeriodEnd ? 'İptali Geri Al' : 'Aboneliği İptal Et'}
                    </button>
                </div>
            )}

            {subscriptionStatus === 'UNPAID' && (
                <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '16px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <AlertTriangle size={22} style={{ color: '#d97706', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#92400e' }}>Ödeme Başarısız</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                            Aylık otomatik çekim yapılamadı. Kart bilgilerinizi güncelleyin.
                        </div>
                    </div>
                </div>
            )}

            {/* Mevcut Plan Özeti ve Ödeme Yöntemi */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '1.5rem' }}>
                <div className="card glass-premium" style={{ display: 'flex', flexDirection: 'column', padding: '2rem', borderTop: '4px solid var(--primary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Mevcut Planınız</div>
                            <h2 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--primary)' }}>{currentPlanData.name}</h2>
                        </div>
                        <div style={{ width: '48px', height: '48px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {currentPlanData.icon}
                        </div>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '2rem', flex: 1 }}>
                        {currentPlanData.description}
                    </p>
                    {currentPlan === 'free' && (
                        <button onClick={() => navigate('/checkout?plan=pro')} style={{ background: 'var(--primary)', color: 'white', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 800, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)' }}>
                            <Zap size={18} /> Planı Yükselt
                        </button>
                    )}
                    {currentPlan === 'pro' && (
                        <button onClick={() => navigate('/checkout?plan=premium')} style={{ background: 'linear-gradient(135deg, var(--primary), #c026d3)', color: 'white', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 800, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)' }}>
                            <Crown size={18} /> Premium'a Yükselt
                        </button>
                    )}
                </div>

                <div className="card glass" style={{ display: 'flex', flexDirection: 'column', padding: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 800 }}>Ödeme Yöntemi</div>
                    </div>
                    {currentPlan === 'free' ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: 'var(--text-muted)' }}>
                            <CreditCard size={32} style={{ opacity: 0.5 }} />
                            <div style={{ fontSize: '0.875rem', textAlign: 'center' }}>Ücretsiz planda olduğunuz için<br/>tanımlı bir ödeme yönteminiz bulunmuyor.</div>
                        </div>
                    ) : tenantData?.card_last_four ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'var(--bg-main)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border)' }}>
                            <div style={{ width: '48px', height: '32px', background: '#1a1a2e', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.05em' }}>
                                {(tenantData.card_brand || 'CARD').toUpperCase().slice(0, 4)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: '0.9375rem', letterSpacing: '0.05em' }}>•••• •••• •••• {tenantData.card_last_four}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{tenantData.card_brand || 'Kredi/Banka Kartı'}</div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'var(--bg-main)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border)' }}>
                            <div style={{ width: '48px', height: '48px', background: 'rgba(var(--primary-rgb), 0.1)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CreditCard size={22} style={{ color: 'var(--primary)' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: '0.9375rem' }}>Kart bilgisi bulunamadı</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Abonelik başlatılırken kart bilgileri iyzico güvenli ortamında saklanır.</div>
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1.5rem', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                        <ShieldCheck size={14} style={{ color: '#10b981' }} /> Ödemeleriniz 256-bit şifreleme ile güvendedir.
                    </div>
                </div>
            </div>

            {/* Plan Karşılaştırması */}
            <div className="card glass" style={{ padding: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>Planınızı Seçin</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>İşletmenizin ihtiyaçlarına en uygun planı bularak büyümeye hız kesmeden devam edin.</p>
                </div>

                {/* Fiyatlandırma Döngüsü Seçici */}
                <div style={{ display: 'flex', background: 'var(--bg-main)', padding: '4px', borderRadius: '14px', marginBottom: '3rem', border: '1px solid var(--border)' }}>
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        style={{ padding: '10px 24px', borderRadius: '10px', fontSize: '0.875rem', fontWeight: 700, border: 'none', background: billingCycle === 'monthly' ? 'var(--bg-card)' : 'transparent', color: billingCycle === 'monthly' ? 'var(--text-main)' : 'var(--text-muted)', boxShadow: billingCycle === 'monthly' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                        Aylık Ödeme
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        style={{ padding: '10px 24px', borderRadius: '10px', fontSize: '0.875rem', fontWeight: 700, border: 'none', background: billingCycle === 'yearly' ? 'var(--bg-card)' : 'transparent', color: billingCycle === 'yearly' ? 'var(--text-main)' : 'var(--text-muted)', boxShadow: billingCycle === 'yearly' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        Yıllık Ödeme <span style={{ background: '#10b98115', color: '#10b981', padding: '2px 6px', borderRadius: '6px', fontSize: '0.625rem' }}>%20 İndirimli</span>
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '1.5rem', width: '100%' }}>
                    {plans.map(plan => {
                        const btnProps = getButtonProps(plan);
                        return (
                            <div key={plan.id} style={{
                                background: plan.isPopular ? 'var(--bg-card)' : 'var(--bg-main)',
                                border: `2px solid ${plan.isPopular ? 'var(--primary)' : plan.id === 'premium' ? '#c026d3' : 'var(--border)'}`,
                                borderRadius: '24px',
                                padding: '2rem',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                boxShadow: plan.isPopular ? '0 20px 40px rgba(var(--primary-rgb), 0.1)' : plan.id === 'premium' ? '0 20px 40px rgba(192, 38, 211, 0.08)' : 'none'
                            }}>
                                {plan.isPopular && (
                                    <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--primary)', color: 'white', padding: '4px 16px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                                        En Çok Tercih Edilen
                                    </div>
                                )}

                                <h4 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>{plan.name}</h4>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '1.5rem', minHeight: '40px' }}>{plan.description}</p>

                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '2rem' }}>
                                    <span style={{ fontSize: '2.25rem', fontWeight: 900 }}>{billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly}</span>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 600 }}>/{billingCycle === 'yearly' ? 'yıl' : 'ay'}</span>
                                </div>

                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2.5rem' }}>
                                    {plan.features.map((feature, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                            {feature.included ? (
                                                <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }} />
                                            ) : (
                                                <XCircle size={18} style={{ color: 'var(--border)', flexShrink: 0, marginTop: '2px' }} />
                                            )}
                                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: feature.included ? 'var(--text-main)' : 'var(--text-muted)' }}>
                                                {feature.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => handlePlanAction(plan.id)}
                                    disabled={btnProps.disabled}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        borderRadius: '12px',
                                        background: btnProps.style === 'current' ? 'var(--bg-main)' : plan.isPopular ? 'var(--primary)' : plan.id === 'premium' ? 'linear-gradient(135deg, var(--primary), #c026d3)' : 'var(--bg-main)',
                                        color: btnProps.style === 'current' ? 'var(--text-muted)' : (plan.isPopular || plan.id === 'premium') ? 'white' : 'var(--text-main)',
                                        fontWeight: 800,
                                        fontSize: '0.9375rem',
                                        cursor: btnProps.disabled ? 'default' : 'pointer',
                                        border: btnProps.style === 'current' ? '1px solid var(--border)' : btnProps.style === 'downgrade' ? '1px solid var(--border)' : '1px solid transparent',
                                        opacity: btnProps.style === 'downgrade' ? 0.6 : 1,
                                    }}>
                                    {btnProps.label}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPayments;
